import React, { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, X, Upload, Loader2, Eye, EyeOff, FileText, Package, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TableSkeletonRows } from "@/components/admin/useAdminData";

const fmt = (n) => Number(n || 0).toFixed(2) + " €";
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }) : "—");
const TYPES = ["digital", "software", "physical"];
const blobToB64 = (blob) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });

const EMPTY = {
  type: "digital", name_de: "", name_en: "", slug: "", short_de: "", short_en: "", desc_de: "", desc_en: "",
  category: "", images: "", price: "", sale_price: "", vat_rate: 0, stock: "", featured: false, published: false,
  version: "", changelog: "", system_requirements: "", download_limit: "", updates_enabled: true,
};

export default function AdminShop() {
  const { lang } = useI18n();
  const de = lang === "de";
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadedRef = useRef(false);

  const loadProducts = () => api.get("/shop/admin/products").then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  const loadCats = () => api.get("/shop/admin/categories").then((r) => setCats(r.data)).catch(() => {});
  const loadOrders = () => api.get("/shop/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => {
    if (loadedRef.current) return; // prevents StrictMode double-fetch
    loadedRef.current = true;
    loadProducts(); loadCats(); loadOrders();
  }, []);

  const togglePublish = async (p) => {
    await api.post(`/shop/admin/products/${p.id}/publish`, { published: !p.published });
    loadProducts();
  };
  const delProduct = async (p) => {
    if (!window.confirm(de ? "Produkt löschen?" : "Delete product?")) return;
    await api.delete(`/shop/admin/products/${p.id}`); loadProducts();
  };

  const tabs = [["products", de ? "Produkte" : "Products"], ["categories", de ? "Kategorien" : "Categories"],
    ["orders", de ? "Bestellungen" : "Orders"]];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-black">Shop</h1>
        {tab === "products" && <button onClick={() => setEditing(EMPTY)} className="btn-primary !py-2 !px-4 text-sm" data-testid="shop-new-product"><Plus size={16} /> {de ? "Neues Produkt" : "New product"}</button>}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-shop-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "products" && (
        <div className="surface overflow-x-auto" data-testid="admin-products-table">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-muted-foreground"><tr>
              <th className="p-4 font-semibold">{de ? "Name" : "Name"}</th><th className="p-4 font-semibold">{de ? "Typ" : "Type"}</th>
              <th className="p-4 font-semibold">{de ? "Preis" : "Price"}</th><th className="p-4 font-semibold">Status</th>
              <th className="p-4 font-semibold text-right">{de ? "Aktionen" : "Actions"}</th>
            </tr></thead>
            <tbody>
              {loading ? <TableSkeletonRows cols={5} /> : products.map((p) => (
                <tr key={p.id} className="border-t border-border" data-testid={`admin-product-${p.id}`}>
                  <td className="p-4 font-medium">{p.name_de || p.name_en}<div className="text-xs text-muted-foreground font-mono">{p.slug}</div></td>
                  <td className="p-4 capitalize">{p.type}</td>
                  <td className="p-4">{Number(p.sale_price || p.price || 0) <= 0 ? <span className="text-green-500 font-semibold">{de ? "Kostenlos" : "Free"}</span> : fmt(p.sale_price || p.price)}{p.free_claims > 0 && <div className="text-[11px] text-muted-foreground">{p.free_claims} {de ? "kostenlos erhalten" : "free claims"}</div>}</td>
                  <td className="p-4"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${p.published ? "bg-green-500/15 text-green-500" : "bg-secondary text-muted-foreground"}`}>{p.published ? (de ? "Veröffentlicht" : "Published") : "Draft"}</span></td>
                  <td className="p-4"><div className="flex gap-1 justify-end">
                    <button onClick={() => togglePublish(p)} className="p-2 hover:text-primary" title="Publish" data-testid={`product-publish-${p.id}`}>{p.published ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                    <button onClick={() => setEditing({ ...EMPTY, ...p, images: (p.images || []).join(", ") })} className="p-2 hover:text-primary" data-testid={`product-edit-${p.id}`}><Pencil size={15} /></button>
                    <button onClick={() => delProduct(p)} className="p-2 hover:text-destructive" data-testid={`product-delete-${p.id}`}><Trash2 size={15} /></button>
                  </div></td>
                </tr>
              ))}
              {!loading && products.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{de ? "Keine Produkte." : "No products."}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "categories" && <Categories de={de} cats={cats} reload={loadCats} />}

      {tab === "orders" && <OrdersTable de={de} orders={orders} reload={loadOrders} />}

      {editing && <ProductDrawer de={de} data={editing} cats={cats} onClose={() => setEditing(null)} onSaved={loadProducts} />}
    </div>
  );
}

function OrdersTable({ de, orders, reload }) {
  const [busy, setBusy] = useState(null);
  const cancel = async (o) => {
    if (!window.confirm(de ? `Bestellung von ${o.customer_email} stornieren?` : `Cancel order from ${o.customer_email}?`)) return;
    setBusy(o.id);
    try { await api.post(`/shop/admin/orders/${o.id}/cancel`); toast.success(de ? "Bestellung storniert" : "Order cancelled"); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const refund = async (o) => {
    if (!window.confirm(de ? `Zahlung für ${fmt(o.total)} erstatten? Dies kann nicht rückgängig gemacht werden.` : `Refund ${fmt(o.total)}? This cannot be undone.`)) return;
    setBusy(o.id);
    try { const { data: r } = await api.post(`/shop/admin/orders/${o.id}/refund`); toast.success(de ? (r.stripe_refunded ? "Erstattet (Stripe)" : "Bestellung erstattet") : "Order refunded"); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const del = async (o) => {
    if (!window.confirm(de
      ? "Möchten Sie diese Bestellung wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden."
      : "Do you really want to permanently delete this order? This action cannot be undone.")) return;
    setBusy(o.id);
    try { await api.delete(`/shop/admin/orders/${o.id}`); toast.success(de ? "Bestellung gelöscht" : "Order deleted"); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const st = (s) => {
    const map = { paid: "bg-green-500/15 text-green-500", pending: "bg-amber-500/15 text-amber-500", cancelled: "bg-secondary text-muted-foreground", refunded: "bg-red-500/15 text-red-500" };
    return map[s] || "bg-amber-500/15 text-amber-500";
  };
  const stLabel = (s) => {
    const m = { paid: "Bezahlt", pending: "Ausstehend", unpaid: "Offen", awaiting_payment: "Zahlung ausstehend", cancelled: "Storniert", refunded: "Erstattet", completed: "Abgeschlossen" };
    return de ? (m[(s || "").toLowerCase()] || s) : s;
  };
  const canCancel = (s) => ["pending", "unpaid", "awaiting_payment"].includes((s || "").toLowerCase());
  const cancelledCount = orders.filter((o) => (o.status || "").toLowerCase() === "cancelled").length;
  const bulkDel = async () => {
    if (!window.confirm(de
      ? `⚠️ ${cancelledCount} stornierte Bestellung(en) endgültig löschen?\n\nDiese Aktion ist UNWIDERRUFLICH. Es werden AUSSCHLIESSLICH stornierte Bestellungen gelöscht — ausstehende, bezahlte, in Bearbeitung befindliche, abgeschlossene oder erstattete Bestellungen bleiben unberührt.`
      : `Permanently delete ${cancelledCount} cancelled order(s)? Irreversible. Only cancelled orders are removed.`)) return;
    setBusy("bulk");
    try { const { data: r } = await api.post("/shop/admin/orders/delete-cancelled"); toast.success(de ? `${r.deleted} Bestellung(en) gelöscht` : `${r.deleted} order(s) deleted`); reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  return (
    <div className="space-y-3">
      {cancelledCount > 0 && (
        <div className="flex justify-end">
          <button onClick={bulkDel} disabled={busy === "bulk"} className="btn-ghost !py-2 !px-4 text-sm text-red-500 hover:text-red-600" data-testid="orders-delete-cancelled">
            {busy === "bulk" ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} {de ? `Alle stornierten Bestellungen löschen (${cancelledCount})` : `Delete all cancelled orders (${cancelledCount})`}
          </button>
        </div>
      )}
      <div className="surface overflow-x-auto" data-testid="admin-orders-table"><table className="w-full text-sm">
      <thead className="bg-secondary text-left text-muted-foreground"><tr>
        <th className="p-4">{de ? "Datum & Uhrzeit" : "Date & time"}</th><th className="p-4">{de ? "Kunde" : "Customer"}</th>
        <th className="p-4">{de ? "Artikel" : "Items"}</th><th className="p-4">{de ? "Gesamt" : "Total"}</th>
        <th className="p-4">Status</th><th className="p-4">{de ? "Rechnung" : "Invoice"}</th>
        <th className="p-4 text-right">{de ? "Aktionen" : "Actions"}</th></tr></thead>
      <tbody>{orders.map((o) => (
        <tr key={o.id} className="border-t border-border" data-testid={`admin-order-${o.id}`}>
          <td className="p-4 whitespace-nowrap text-xs" data-testid={`order-date-${o.id}`}>{fmtDT(o.created_at)}</td>
          <td className="p-4">{o.customer_email}</td>
          <td className="p-4">{(o.items || []).map((i) => `${i.name} ×${i.qty}`).join(", ")}</td>
          <td className="p-4 font-semibold">{o.is_free ? <span className="text-green-500">{de ? "Kostenlos" : "Free"}</span> : fmt(o.total)}</td>
          <td className="p-4"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${o.is_free ? "bg-green-500/15 text-green-500" : st(o.status)}`}>{o.is_free ? (de ? "Kostenlos" : "Free") : stLabel(o.status)}</span></td>
          <td className="p-4 font-mono text-xs">{o.invoice_number || "—"}</td>
          <td className="p-4"><div className="flex gap-1 justify-end">
            {canCancel(o.status) && <button onClick={() => cancel(o)} disabled={busy === o.id} className="btn-ghost !py-1.5 !px-3 text-xs text-red-500 hover:text-red-600" data-testid={`order-cancel-${o.id}`}>{busy === o.id ? <Loader2 size={13} className="animate-spin" /> : <><Ban size={13} /> {de ? "Stornieren" : "Cancel"}</>}</button>}
            {o.status === "paid" && <button onClick={() => refund(o)} disabled={busy === o.id} className="btn-ghost !py-1.5 !px-3 text-xs text-amber-500 hover:text-amber-600" data-testid={`order-refund-${o.id}`}>{busy === o.id ? <Loader2 size={13} className="animate-spin" /> : <><RotateCcw size={13} /> {de ? "Erstatten" : "Refund"}</>}</button>}
            <button onClick={() => del(o)} disabled={busy === o.id} className="btn-ghost !py-1.5 !px-3 text-xs text-red-500 hover:text-red-600" data-testid={`order-delete-${o.id}`}>{busy === o.id ? <Loader2 size={13} className="animate-spin" /> : <><Trash2 size={13} /> {de ? "Löschen" : "Delete"}</>}</button>
          </div></td>
        </tr>
      ))}
        {orders.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">—</td></tr>}</tbody>
    </table></div>
    </div>
  );
}

function Categories({ de, cats, reload }) {
  const blank = { name_de: "", name_en: "", slug: "", sort: 0, published: true };
  const [form, setForm] = useState(blank);
  const save = async () => {
    if (!form.name_de) { toast.error(de ? "Name erforderlich" : "Name required"); return; }
    try { await api.post("/shop/admin/categories", form); toast.success("OK"); setForm(blank); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const togglePub = async (c) => { await api.post(`/shop/admin/categories/${c.id}/publish`, { published: !(c.published !== false) }); reload(); };
  const del = async (id) => { if (!window.confirm(de ? "Kategorie löschen?" : "Delete category?")) return; await api.delete(`/shop/admin/categories/${id}`); reload(); };
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="surface p-5 space-y-3 h-fit" data-testid="category-form">
        <h3 className="font-semibold">{form.id ? (de ? "Kategorie bearbeiten" : "Edit category") : (de ? "Kategorie hinzufügen" : "Add category")}</h3>
        <input value={form.name_de} onChange={(e) => setForm({ ...form, name_de: e.target.value })} placeholder="Name (DE)" className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm" data-testid="cat-name-de" />
        <input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} placeholder="Name (EN)" className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm" data-testid="cat-name-en" />
        <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder={de ? "Slug (automatisch)" : "Slug (auto)"} className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm" data-testid="cat-slug" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.published !== false} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4 accent-[hsl(21,100%,50%)]" data-testid="cat-published" /> {de ? "Veröffentlicht" : "Published"}</label>
        <div className="flex gap-2">
          <button onClick={save} className="btn-primary flex-1 !py-2 text-sm" data-testid="cat-save"><Plus size={15} /> {de ? "Speichern" : "Save"}</button>
          {form.id && <button onClick={() => setForm(blank)} className="btn-ghost !py-2 text-sm">{de ? "Neu" : "New"}</button>}
        </div>
      </div>
      <div className="md:col-span-2 surface divide-y divide-border">
        {cats.map((c) => (
          <div key={c.id} className="flex items-center justify-between p-4" data-testid={`category-${c.id}`}>
            <div className="min-w-0">
              <div className="font-medium flex items-center gap-2">{c.name_de}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.published !== false ? "bg-green-500/15 text-green-500" : "bg-secondary text-muted-foreground"}`}>{c.published !== false ? (de ? "Aktiv" : "Live") : (de ? "Versteckt" : "Hidden")}</span>
              </div>
              <div className="text-xs text-muted-foreground font-mono">{c.slug} · {c.count || 0} {de ? "Produkte" : "products"}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => togglePub(c)} className="p-2 hover:text-primary" title="Publish" data-testid={`cat-publish-${c.id}`}>{c.published !== false ? <Eye size={15} /> : <EyeOff size={15} />}</button>
              <button onClick={() => setForm({ id: c.id, name_de: c.name_de, name_en: c.name_en || "", slug: c.slug, sort: c.sort || 0, published: c.published !== false })} className="p-2 hover:text-primary" data-testid={`cat-edit-${c.id}`}><Pencil size={15} /></button>
              <button onClick={() => del(c.id)} className="p-2 text-muted-foreground hover:text-destructive" data-testid={`cat-delete-${c.id}`}><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        {cats.length === 0 && <p className="p-8 text-center text-muted-foreground">—</p>}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs font-semibold text-muted-foreground">{label}</span><div className="mt-1">{children}</div></label>;
}
const inputCls = "w-full rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary";

function ProductDrawer({ de, data, cats, onClose, onSaved }) {
  const [f, setF] = useState(data);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [files, setFiles] = useState(data.files || []);
  const fileRef = useRef();
  const imgRef = useRef();
  const [imgUploading, setImgUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isDigital = ["digital", "software"].includes(f.type);
  const imagesList = String(f.images || "").split(",").map((s) => s.trim()).filter(Boolean);

  // Ensure the product exists (auto-create on first upload during "Neues Produkt"),
  // so image/file uploads work during creation — not only when editing.
  const ensureSaved = async () => {
    if (f.id) return f.id;
    if (!f.name_de && !f.name_en) { toast.error(de ? "Bitte zuerst einen Produktnamen eingeben." : "Please enter a product name first."); return null; }
    const payload = { ...f, images: String(f.images || "").split(",").map((s) => s.trim()).filter(Boolean) };
    const { data: saved } = await api.post("/shop/admin/products", payload);
    setF((p) => ({ ...p, id: saved.id }));
    onSaved();
    return saved.id;
  };

  const doImageUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImgUploading(true);
    try {
      const pid = await ensureSaved();
      if (!pid) return;
      const b64 = await blobToB64(file);
      const { data: r } = await api.post(`/shop/admin/products/${pid}/images`, { data: b64, mime: file.type });
      set("images", (r.images || []).join(", "));
      toast.success(de ? "Bild hochgeladen" : "Image uploaded");
    } catch (err) { toast.error(apiErr(err)); } finally { setImgUploading(false); if (imgRef.current) imgRef.current.value = ""; }
  };

  const delImage = async (url) => {
    if (f.id && url.startsWith("/api/")) {
      try { const { data: r } = await api.delete(`/shop/admin/products/${f.id}/images`, { data: { url } }); set("images", (r.images || []).join(", ")); return; }
      catch (err) { toast.error(apiErr(err)); return; }
    }
    set("images", imagesList.filter((u) => u !== url).join(", "));
  };

  const save = async () => {
    if (!f.name_de && !f.name_en) { toast.error(de ? "Name erforderlich" : "Name required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, images: String(f.images || "").split(",").map((s) => s.trim()).filter(Boolean) };
      const { data: saved } = await api.post("/shop/admin/products", payload);
      set("id", saved.id);
      toast.success(de ? "Gespeichert" : "Saved");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const doUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const GB = 1024 * 1024 * 1024;
    if (file.size > GB) { toast.error(de ? "Datei zu groß (max. 1 GB)" : "File too large (max 1 GB)"); if (fileRef.current) fileRef.current.value = ""; return; }
    setUploading(true); setUploadPct(0);
    const CHUNK = 10 * 1024 * 1024; // 10 MB raw per request
    try {
      const pid = await ensureSaved();
      if (!pid) { setUploading(false); if (fileRef.current) fileRef.current.value = ""; return; }
      const { data: init } = await api.post("/shop/admin/upload/init", {});
      const fileId = init.file_id;
      let start = 0;
      while (start < file.size) {
        const end = Math.min(start + CHUNK, file.size);
        const b64 = await blobToB64(file.slice(start, end));
        // retry/resume: up to 3 attempts per chunk
        let ok = false, lastErr;
        for (let attempt = 0; attempt < 3 && !ok; attempt++) {
          try {
            await api.post("/shop/admin/upload/chunk", { file_id: fileId, offset: start, chunk: b64 }, { timeout: 300000 });
            ok = true;
          } catch (err) {
            lastErr = err;
            // resume from server-confirmed size
            try { const { data: s } = await api.post("/shop/admin/upload/status", { file_id: fileId }); if (s.size >= end) { ok = true; } } catch (_) {}
            if (!ok) await new Promise((r) => setTimeout(r, 800));
          }
        }
        if (!ok) throw lastErr;
        start = end;
        setUploadPct(Math.round((start / file.size) * 100));
      }
      const { data: fin } = await api.post(`/shop/admin/products/${pid}/finalize-file`, { file_id: fileId, filename: file.name, version: f.version, changelog: f.changelog });
      setFiles(fin.files); toast.success(de ? "Datei hochgeladen" : "File uploaded");
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); setUploadPct(0); if (fileRef.current) fileRef.current.value = ""; }
  };

  const delFile = async (fid) => {
    const { data: r } = await api.delete(`/shop/admin/products/${f.id}/files/${fid}`);
    setFiles(r.files);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="product-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-background h-full overflow-y-auto">
        <div className="sticky top-0 bg-background border-b border-border flex items-center justify-between p-5 z-10">
          <h2 className="font-display font-bold text-lg">{f.id ? (de ? "Produkt bearbeiten" : "Edit product") : (de ? "Neues Produkt" : "New product")}</h2>
          <button onClick={onClose} data-testid="drawer-close"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={de ? "Typ" : "Type"}><select value={f.type} onChange={(e) => set("type", e.target.value)} className={inputCls} data-testid="pf-type">{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
            <Field label={de ? "Kategorie" : "Category"}><select value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="pf-category"><option value="">—</option>{cats.map((c) => <option key={c.id} value={c.slug}>{c.name_de}</option>)}</select></Field>
            <Field label="Name (DE)"><input value={f.name_de} onChange={(e) => set("name_de", e.target.value)} className={inputCls} data-testid="pf-name-de" /></Field>
            <Field label="Name (EN)"><input value={f.name_en} onChange={(e) => set("name_en", e.target.value)} className={inputCls} data-testid="pf-name-en" /></Field>
            <Field label="Slug"><input value={f.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto" className={inputCls} data-testid="pf-slug" /></Field>
            <Field label={de ? "Bild-URL einfügen (optional)" : "Paste image URL (optional)"}><input value={f.images} onChange={(e) => set("images", e.target.value)} placeholder={de ? "URLs mit Komma trennen" : "comma-separated URLs"} className={inputCls} data-testid="pf-images" /></Field>
          </div>

          <div className="surface p-4 space-y-3">
            <span className="text-xs font-semibold text-muted-foreground">{de ? "Produktbilder" : "Product images"}</span>
            <div className="flex flex-wrap gap-3" data-testid="pf-image-gallery">
              {imagesList.map((url) => (
                <div key={url} className="relative h-20 w-20 rounded-md overflow-hidden border border-border group" data-testid={`pf-image-${url.split("/").pop()}`}>
                  <img src={url.startsWith("http") ? url : url} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => delImage(url)} className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="pf-image-remove"><X size={12} /></button>
                </div>
              ))}
              {imagesList.length === 0 && <p className="text-xs text-muted-foreground">{de ? "Noch keine Bilder." : "No images yet."}</p>}
            </div>
            <input ref={imgRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={doImageUpload} data-testid="pf-image-input" />
            <button onClick={() => imgRef.current.click()} disabled={imgUploading} className="btn-ghost !py-2 !px-4 text-sm" data-testid="pf-image-upload-btn">
              {imgUploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {de ? "Bild vom Computer hochladen" : "Upload image from computer"}
            </button>
            <p className="text-[11px] text-muted-foreground">PNG, JPG, JPEG, WEBP · max. 5 MB</p>
          </div>
          <Field label={de ? "Kurzbeschreibung (DE)" : "Short (DE)"}><input value={f.short_de} onChange={(e) => set("short_de", e.target.value)} className={inputCls} data-testid="pf-short-de" /></Field>
          <Field label={de ? "Kurzbeschreibung (EN)" : "Short (EN)"}><input value={f.short_en} onChange={(e) => set("short_en", e.target.value)} className={inputCls} /></Field>
          <Field label={de ? "Beschreibung (DE)" : "Description (DE)"}><textarea value={f.desc_de} onChange={(e) => set("desc_de", e.target.value)} rows={4} className={inputCls} data-testid="pf-desc-de" /></Field>
          <Field label={de ? "Beschreibung (EN)" : "Description (EN)"}><textarea value={f.desc_en} onChange={(e) => set("desc_en", e.target.value)} rows={4} className={inputCls} /></Field>

          {f.type !== "subscription" && (
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label={de ? "Preis (€)" : "Price (€)"}><input type="number" step="0.01" value={f.price} onChange={(e) => set("price", e.target.value)} className={inputCls} data-testid="pf-price" /></Field>
              <Field label={de ? "Angebotspreis (€)" : "Sale price (€)"}><input type="number" step="0.01" value={f.sale_price} onChange={(e) => set("sale_price", e.target.value)} className={inputCls} /></Field>
          <p className="text-xs text-muted-foreground">Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.</p>
            </div>
          )}

          {f.type === "physical" && <Field label={de ? "Lagerbestand" : "Stock"}><input type="number" value={f.stock} onChange={(e) => set("stock", e.target.value)} className={inputCls} data-testid="pf-stock" /></Field>}
          {f.type === "physical" && Number(f.sale_price || f.price || 0) <= 0 && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.allow_free_physical} onChange={(e) => set("allow_free_physical", e.target.checked)} className="h-4 w-4 accent-[hsl(21,100%,50%)]" data-testid="pf-allow-free-physical" /> {de ? "Kostenlose Abgabe dieses physischen Produkts erlauben" : "Allow free acquisition of this physical product"}</label>
          )}
          {Number(f.sale_price || f.price || 0) <= 0 && ["digital", "software"].includes(f.type) && (
            <p className="text-xs text-green-500" data-testid="pf-free-hint">{de ? "Preis 0,00 € → Kostenloses Produkt. Kunden erhalten es ohne Zahlung." : "Price 0.00 € → free product. Customers get it without payment."}</p>
          )}

          {(isDigital) && (
            <div className="surface p-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={de ? "Version" : "Version"}><input value={f.version} onChange={(e) => set("version", e.target.value)} className={inputCls} data-testid="pf-version" /></Field>
                <Field label={de ? "Download-Limit" : "Download limit"}><input type="number" value={f.download_limit} onChange={(e) => set("download_limit", e.target.value)} placeholder={de ? "unbegrenzt" : "unlimited"} className={inputCls} /></Field>
              </div>
              <Field label="Changelog"><textarea value={f.changelog} onChange={(e) => set("changelog", e.target.value)} rows={2} className={inputCls} /></Field>
              {f.type === "software" && <Field label={de ? "Systemanforderungen" : "System requirements"}><textarea value={f.system_requirements} onChange={(e) => set("system_requirements", e.target.value)} rows={2} className={inputCls} /></Field>}
              <div>
                <span className="text-xs font-semibold text-muted-foreground">{de ? "Dateien" : "Files"}</span>
                <div className="mt-2 space-y-2">
                  {files.map((file) => (
                    <div key={file.file_id} className="flex items-center justify-between text-sm surface p-2.5" data-testid={`pf-file-${file.file_id}`}>
                      <span className="flex items-center gap-2 min-w-0"><FileText size={15} className="text-primary shrink-0" /><span className="truncate">{file.filename}</span><span className="text-xs text-muted-foreground">{Math.round(file.size / 1024)} KB</span></span>
                      <button onClick={() => delFile(file.file_id)} className="p-1 text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
                <input ref={fileRef} type="file" className="hidden" onChange={doUpload} data-testid="pf-file-input" />
                <button onClick={() => fileRef.current.click()} disabled={uploading} className="btn-ghost !py-2 !px-4 text-sm mt-2" data-testid="pf-upload-btn">
                  {uploading ? <><Loader2 size={15} className="animate-spin" /> {de ? "Wird hochgeladen" : "Uploading"} {uploadPct}%</> : <><Upload size={15} /> {de ? "Datei hochladen" : "Upload file"}</>}
                </button>
                {uploading && <div className="mt-2 h-2 w-full rounded-full bg-secondary overflow-hidden" data-testid="pf-upload-progress"><div className="h-full bg-primary transition-all" style={{ width: `${uploadPct}%` }} /></div>}
                <p className="text-[11px] text-muted-foreground mt-1">{de ? "Max. 1 GB · Upload kann bei Unterbrechung fortgesetzt werden" : "Max 1 GB · resumable on interruption"}</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[hsl(21,100%,50%)]" /> {de ? "Hervorgehoben" : "Featured"}</label>
            <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={!!f.published} onChange={(e) => set("published", e.target.checked)} className="h-4 w-4 accent-[hsl(21,100%,50%)]" data-testid="pf-published" /> {de ? "Veröffentlicht" : "Published"}</label>
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving} className="btn-primary flex-1" data-testid="pf-save">{saving ? <Loader2 size={16} className="animate-spin" /> : (de ? "Speichern" : "Save")}</button>
            <button onClick={onClose} className="btn-ghost">{de ? "Schließen" : "Close"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
